import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import api from '../../utils/api';
import { Award, FileText, Download, Plus, Copy, ExternalLink } from 'lucide-react';

export default function DashboardPage() {
  const { institution } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sub, setSub] = useState(null);

  useEffect(() => {
    api.get('/certificates/dashboard/stats')
      .then(res => {
        setStats(res.data.stats);
        setRecent(res.data.recent);
      })
      .finally(() => setLoading(false));
    api.get('/institutions/subscription/status').then(res => setSub(res.data)).catch(() => {});
  }, []);

  const publicUrl = `${window.location.origin}/form/${institution?.slug}`;

  const copyLink = () => {
    navigator.clipboard.writeText(publicUrl);
    alert('הקישור הועתק!');
  };

  if (loading) return <div>טוען...</div>;

  return (
    <div>
      <div className="page-header">
        <h1>שלום, {institution?.name}</h1>
        <p>סקירה כללית של המערכת</p>
      </div>

      {sub?.is_trial && sub?.days_left !== null && (
        <div className="card" style={{marginBottom: 20, background: sub.days_left <= 5 ? '#fef2f2' : '#fffbeb', border: `1px solid ${sub.days_left <= 5 ? '#fecaca' : '#fde68a'}`}}>
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap: 16}}>
            <div>
              <div style={{fontSize:14, fontWeight:600, color: sub.days_left <= 5 ? '#dc2626' : '#d97706'}}>
                {sub.days_left > 0 ? `נותרו ${sub.days_left} ימי ניסיון` : 'תקופת הניסיון הסתיימה'}
              </div>
              <div style={{fontSize:13, color:'var(--text-muted)', marginTop:2}}>
                שדרג לתוכנית בתשלום כדי להמשיך ליהנות מכל התכונות
              </div>
            </div>
            <a href="/payment" className="btn btn-primary">שדרוג עכשיו</a>
          </div>
        </div>
      )}

      {/* קישור ציבורי */}
      <div className="card" style={{marginBottom: 24, background: 'var(--primary-light)', border: '1px solid #bfdbfe'}}>
        <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap: 16}}>
          <div>
            <div style={{fontSize:13, color:'var(--primary)', fontWeight:600, marginBottom:4}}>קישור הטופס הציבורי שלך</div>
            <div style={{fontSize:14, color:'var(--text)', fontFamily:'monospace'}}>{publicUrl}</div>
          </div>
          <div style={{display:'flex', gap:8}}>
            <button className="btn btn-secondary" onClick={copyLink}>
              <Copy size={16} /> העתק
            </button>
            <a className="btn btn-primary" href={publicUrl} target="_blank" rel="noreferrer">
              <ExternalLink size={16} /> פתח
            </a>
          </div>
        </div>
      </div>

      {/* סטטיסטיקות */}
      <div className="grid-3" style={{marginBottom: 28}}>
        <div className="card">
          <div style={{display:'flex', alignItems:'center', gap:12}}>
            <div style={{width:44, height:44, background:'var(--primary-light)', borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center'}}>
              <Award size={22} color="var(--primary)" />
            </div>
            <div>
              <div style={{fontSize:28, fontWeight:700}}>{stats?.total_certificates || 0}</div>
              <div style={{fontSize:13, color:'var(--text-muted)'}}>סה"כ אישורים</div>
            </div>
          </div>
        </div>

        <div className="card">
          <div style={{display:'flex', alignItems:'center', gap:12}}>
            <div style={{width:44, height:44, background:'#dcfce7', borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center'}}>
              <FileText size={22} color="var(--success)" />
            </div>
            <div>
              <div style={{fontSize:28, fontWeight:700}}>{stats?.completed || 0}</div>
              <div style={{fontSize:13, color:'var(--text-muted)'}}>הושלמו</div>
            </div>
          </div>
        </div>

        <div className="card">
          <div style={{display:'flex', alignItems:'center', gap:12}}>
            <div style={{width:44, height:44, background:'#fef9c3', borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center'}}>
              <Download size={22} color="var(--warning)" />
            </div>
            <div>
              <div style={{fontSize:28, fontWeight:700}}>{stats?.total_downloads || 0}</div>
              <div style={{fontSize:13, color:'var(--text-muted)'}}>הורדות</div>
            </div>
          </div>
        </div>
      </div>

      {/* פעולות מהירות */}
      <div style={{display:'flex', gap:12, marginBottom:28}}>
        <Link to="/certificates/new" className="btn btn-primary">
          <Plus size={18} /> יצירת אישור
        </Link>
        <Link to="/templates/new" className="btn btn-secondary">
          <FileText size={18} /> תבנית חדשה
        </Link>
      </div>

      {/* אישורים אחרונים */}
      <div className="card">
        <h2 style={{fontSize:16, fontWeight:600, marginBottom:16}}>אישורים אחרונים</h2>
        {recent.length === 0 ? (
          <p style={{color:'var(--text-muted)', textAlign:'center', padding:'20px 0'}}>אין אישורים עדיין</p>
        ) : (
          <table style={{width:'100%', borderCollapse:'collapse'}}>
            <thead>
              <tr style={{borderBottom:'1px solid var(--border)'}}>
                <th style={{textAlign:'right', padding:'8px 12px', fontSize:12, color:'var(--text-muted)', fontWeight:500}}>שם</th>
                <th style={{textAlign:'right', padding:'8px 12px', fontSize:12, color:'var(--text-muted)', fontWeight:500}}>תבנית</th>
                <th style={{textAlign:'right', padding:'8px 12px', fontSize:12, color:'var(--text-muted)', fontWeight:500}}>תאריך</th>
                <th style={{textAlign:'right', padding:'8px 12px', fontSize:12, color:'var(--text-muted)', fontWeight:500}}>הורדות</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {recent.map(cert => (
                <tr key={cert.id} style={{borderBottom:'1px solid var(--border)'}}>
                  <td style={{padding:'10px 12px', fontSize:14}}>{cert.requester_name || '—'}</td>
                  <td style={{padding:'10px 12px', fontSize:14, color:'var(--text-muted)'}}>{cert.template_name || '—'}</td>
                  <td style={{padding:'10px 12px', fontSize:13, color:'var(--text-muted)'}}>{new Date(cert.created_at).toLocaleDateString('he-IL')}</td>
                  <td style={{padding:'10px 12px', fontSize:14}}>{cert.download_count}</td>
                  <td style={{padding:'10px 12px'}}>
                    <a
                      href={`/api/certificates/${cert.id}/download`}
                      className="btn btn-secondary"
                      style={{padding:'5px 12px', fontSize:12}}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Download size={14} /> הורדה
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
